/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import {
  ExemplosPassouFalhouVerticalField,
  ExemplosPassouFalhouVerticalFieldOptions,
} from '../../components/ExemplosPassouFalhouVerticalField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta01ExemplosEnum,
  ConsultaSeguimentoPergunta01Model,
} from './model-consultaSeguimentoPergunta01'
import {
  hasPergunta01ExemplosFalhouAnySim,
  hasPergunta01ExemplosPassouAnySim,
  isPergunta01ExemplosAllFilled,
  isPergunta01ExemplosAllNao,
} from './utils-consultaSeguimentoPergunta01'

interface ConsultaSeguimentoPergunta01FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta01Model>
}

export function ConsultaSeguimentoPergunta01Field(props: Readonly<ConsultaSeguimentoPergunta01FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta01Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta01Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_01_HEADER === SimNaoEnum.SIM
  const isPerguntaExemplosAllNao = isPergunta01ExemplosAllNao(value?.PERGUNTA_01_EXEMPLOS)
  const hasPerguntaExemplosPassouAnySim = hasPergunta01ExemplosPassouAnySim(value?.PERGUNTA_01_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta01ExemplosFalhouAnySim(value?.PERGUNTA_01_EXEMPLOS)
  const hasSimOnlyInExemplosPassou = hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim
  const hasSimOnlyInExemplosFalhou = hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim

  const textResultadoFalhouWithAnySim = hasSimOnlyInExemplosFalhou
    ? 'Sim somente para exemplos Falhou:'
    : 'Os mais frequentes são exemplos de Falhou:'
  const textResultadoFalhou = isPerguntaExemplosAllNao ? 'Não para todos' : textResultadoFalhouWithAnySim
  const textResultadoPassou = hasSimOnlyInExemplosPassou
    ? 'Sim somente para exemplos Passou:'
    : 'Os mais frequentes são exemplos de Passou:'

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_01}
          title='“Se você apontar para algum objeto no quarto, o seu filho olha para este objeto?”'
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_01_HEADER} />}
        />
        {!isEmpty(value.PERGUNTA_01_HEADER) && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              <ConsultaSeguimentoIndicatorHelper
                title={
                  isPerguntaHeaderSim
                    ? '“Por favor, me dê um exemplo de como ele responde se você apontar para algo.”'
                    : '“Quando você aponta para algo, o que o seu filho, em geral, faz?”'
                }
                subtitle={
                  isPerguntaHeaderSim &&
                  '(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                }
              />
            </VFlow>
            <VFlow vSpacing={0}>
              <ExemplosPassouFalhouVerticalField<ConsultaSeguimentoPergunta01ExemplosEnum>
                name={name.PERGUNTA_01_EXEMPLOS}
                label='Responda todos os exemplos, conforme as respostas do genitor'
                options={PERGUNTA_01_EXEMPLOS_OPTIONS}
                required
              />
              {hasPerguntaExemplosPassouAnySim && hasPerguntaExemplosFalhouAnySim && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoIndicatorStep
                    text={
                      <VFlow vSpacing={0}>
                        <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                        <Heading level={3} color='inherit'>
                          Pergunta sobre a frequência
                        </Heading>
                      </VFlow>
                    }
                    style={styles.indicatorStep}
                  />
                  <RadioGroupBoxField
                    name={name.PERGUNTA_01_MAIOR_FREQUENCIA}
                    label='Quais os que ele faz com mais frequência?'
                    options={PERGUNTA_01_MAIOR_FREQUENCIA_OPTIONS}
                    required
                  />
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{
            FALHOU: textResultadoFalhou,
            PASSOU: textResultadoPassou,
          }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta01Model): boolean => {
  if (!isPergunta01ExemplosAllFilled(values?.PERGUNTA_01_EXEMPLOS)) {
    return false
  }

  const isPerguntaExemplosAllNao = isPergunta01ExemplosAllNao(values?.PERGUNTA_01_EXEMPLOS)
  const hasPerguntaExemplosPassouAnySim = hasPergunta01ExemplosPassouAnySim(values?.PERGUNTA_01_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta01ExemplosFalhouAnySim(values?.PERGUNTA_01_EXEMPLOS)

  if (
    isPerguntaExemplosAllNao ||
    (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) ||
    (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim)
  ) {
    return true
  } else {
    return !isEmpty(values.PERGUNTA_01_MAIOR_FREQUENCIA)
  }
}

const calculateResultado = (values: ConsultaSeguimentoPergunta01Model): PassouFalhouEnum => {
  const isPerguntaExemplosAllNao = isPergunta01ExemplosAllNao(values?.PERGUNTA_01_EXEMPLOS)
  const hasPerguntaExemplosPassouAnySim = hasPergunta01ExemplosPassouAnySim(values?.PERGUNTA_01_EXEMPLOS)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta01ExemplosFalhouAnySim(values?.PERGUNTA_01_EXEMPLOS)

  if (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) {
    return PassouFalhouEnum.PASSOU
  } else if ((hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim) || isPerguntaExemplosAllNao) {
    return PassouFalhouEnum.FALHOU
  } else {
    return values.PERGUNTA_01_MAIOR_FREQUENCIA
  }
}

const PERGUNTA_01_EXEMPLOS_OPTIONS: ExemplosPassouFalhouVerticalFieldOptions<ConsultaSeguimentoPergunta01ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Olha para o objeto',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.OLHA_PARA_OBJETO,
    },
    {
      label: 'Aponta para o objeto',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.APONTA_PARA_OBJETO,
    },
    {
      label: 'Olha e comenta sobre o objeto',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.OLHA_E_COMENTA_SOBRE_OBJETO,
    },
    {
      label: 'Olha quando o genitor aponta e diz “olhe!”',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.OLHA_QUANDO_GENITOR_APONTA,
    },
  ],
  FALHOU: [
    {
      label: 'Ele/ela ignora você',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.IGNORA_VOCE,
    },
    {
      label: 'Olha ao redor do quarto sem fixar nenhum objeto',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.OLHA_AO_REDOR,
    },
    {
      label: 'Olha para o dedo do genitor',
      value: ConsultaSeguimentoPergunta01ExemplosEnum.OLHA_PARA_DEDO_DO_GENITOR,
    },
  ],
}

const PERGUNTA_01_MAIOR_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
}
